import IcCheckCircle from '@/assets/icons/ic-check-circle.svg';
import IcRefresh from '@/assets/icons/ic-refresh-01.svg';
import IcImage from '@/assets/icons/ic-image.svg';
import { cn, createIconElement, PRODUCT_TYPE_LABELS } from '@/lib';
import { formatCurrency } from '@/lib/currency';
import type { FunnelPageItem, FunnelPageProductInfo } from '../../../types';
import type { JourneyStep } from './funnelJourney.logic';
import {
    buildFunnelPageTree,
    buildFunnelPageTreePositions,
    type FunnelPageRelationship,
    type FunnelPageSlot,
    getFunnelPageBranchTag,
    getFunnelPageIcon,
    getFunnelPageRelationships,
    sortFunnelPageBranches,
} from '../../../logic';
import { StanButton } from '@/components';

interface FunnelJourneyCompletionCardProps {
    history: JourneyStep[];
    publishedPages: FunnelPageItem[];
    earnedCents: number;
    currency: string;
    productInfoByPageId: Record<number, FunnelPageProductInfo | undefined>;
    onRestart: () => void;
    onExit: () => void;
}

type LogEntry =
    | { kind: 'visited'; page: FunnelPageItem; action: 'buy' | 'skip'; historyIndex: number }
    | { kind: 'unseen'; page: FunnelPageItem };

type PageSlot = Extract<FunnelPageSlot, { kind: 'page' }>;

export const FunnelJourneyCompletionCard = ({
    history,
    publishedPages,
    earnedCents,
    currency,
    productInfoByPageId,
    onRestart,
    onExit,
}: FunnelJourneyCompletionCardProps) => {
    const purchases = history.filter(s => s.action === 'buy');
    const skips = history.filter(s => s.action === 'skip');

    const historyByPageId = new Map<number, { action: 'buy' | 'skip'; index: number }>();
    history.forEach((step, index) => {
        historyByPageId.set(step.page.page_id, { action: step.action, index });
    });
    const treeSlots = buildFunnelPageTree(publishedPages);
    const positionByPageId = buildFunnelPageTreePositions(treeSlots);
    const relationshipByPageId = getFunnelPageRelationships(publishedPages);

    return (
        <div className='bg-canvas shadow-stan animate-slide-up w-full rounded-2xl p-6'>
            {/* Header */}
            <div className='mb-6 flex gap-3'>
                <div className='bg-success-subtle flex size-10 shrink-0 items-center justify-center rounded-full max-md:hidden'>
                    <IcCheckCircle className='text-success size-5' />
                </div>
                <div>
                    <h3 className='text-heading-h3 text-dark'>Journey complete!</h3>
                    <p className='text-body-xs text-gray mt-1'>Here&apos;s how this buyer moved through your funnel.</p>
                </div>
            </div>

            {/* Stats row */}
            <div className='border-neutral-subtle divide-neutral-subtle mb-5 grid grid-cols-3 divide-x rounded-lg border'>
                <StatCell
                    value={history.length}
                    label={history.length === 1 ? 'Page seen' : 'Pages seen'}
                    tone='dark'
                />
                <StatCell
                    value={purchases.length}
                    label={purchases.length === 1 ? 'Purchase' : 'Purchases'}
                    tone='success'
                />
                <StatCell value={skips.length} label={skips.length === 1 ? 'Skip' : 'Skips'} tone='dark' />
            </div>

            {/* Revenue highlight */}
            {earnedCents > 0 && (
                <div className='bg-brand-subtle mb-5 flex items-center justify-between rounded-md px-4 py-3'>
                    <p className='text-caption-2-bold text-brand-main tracking-wide uppercase'>
                        Revenue from this buyer
                    </p>
                    <p className='text-heading-h3 text-brand-main'>{formatCurrency(earnedCents / 100, currency)}</p>
                </div>
            )}

            {/* Journey log */}
            {publishedPages.length > 0 && (
                <div className='mb-5 flex flex-col gap-4'>
                    <p className='text-caption-2-bold text-gray tracking-wide uppercase'>Journey log</p>
                    <div className='flex flex-col gap-2'>
                        {treeSlots.map(slot => (
                            <JourneyLogTreeNode
                                key={slot.kind === 'page' ? slot.page.page_id : `s-${slot.stepNumber}`}
                                slot={slot}
                                historyByPageId={historyByPageId}
                                productInfoByPageId={productInfoByPageId}
                                currency={currency}
                                positionByPageId={positionByPageId}
                                relationshipByPageId={relationshipByPageId}
                                parentStepNumber={null}
                            />
                        ))}
                    </div>
                </div>
            )}

            {/* Actions */}
            <div className='flex gap-2'>
                <StanButton name='funnel-journey-completion-exit' variant='outline' className='flex-1' onClick={onExit}>
                    Exit Preview
                </StanButton>
                <StanButton
                    name='funnel-journey-completion-restart'
                    className='flex-[2]'
                    icon={IcRefresh}
                    onClick={onRestart}
                >
                    Try again
                </StanButton>
            </div>
        </div>
    );
};

const StatCell = ({ value, label, tone }: { value: number; label: string; tone: 'dark' | 'success' }) => (
    <div className='flex flex-col items-center gap-0.5 py-3'>
        <span className={cn('text-heading-h3', tone === 'success' ? 'text-success' : 'text-dark')}>{value}</span>
        <span className='text-caption-2-regular text-gray'>{label}</span>
    </div>
);

interface JourneyLogTreeNodeProps {
    slot: FunnelPageSlot;
    historyByPageId: Map<number, { action: 'buy' | 'skip'; index: number }>;
    productInfoByPageId: Record<number, FunnelPageProductInfo | undefined>;
    currency: string;
    positionByPageId: Map<number, number>;
    relationshipByPageId: Map<number, FunnelPageRelationship>;
    parentStepNumber: number | null;
}

const JourneyLogTreeNode = ({
    slot,
    historyByPageId,
    productInfoByPageId,
    currency,
    positionByPageId,
    relationshipByPageId,
    parentStepNumber,
}: JourneyLogTreeNodeProps) => {
    if (slot.kind !== 'page') return null;

    const { page } = slot;
    const historyEntry = historyByPageId.get(page.page_id);
    const entry: LogEntry = historyEntry
        ? { kind: 'visited', page, action: historyEntry.action, historyIndex: historyEntry.index }
        : { kind: 'unseen', page };
    const relationship = relationshipByPageId.get(page.page_id);
    const stepNumber = positionByPageId.get(page.page_id) ?? slot.stepNumber;
    const branchChildren = (slot.children ?? []).filter((c): c is PageSlot => c.kind === 'page');
    const sortedBranches = sortFunnelPageBranches(branchChildren, relationshipByPageId);

    return (
        <div className='flex flex-col gap-2'>
            <JourneyLogRow
                entry={entry}
                info={productInfoByPageId[page.page_id]}
                currency={currency}
                relationship={relationship}
                parentStepNumber={parentStepNumber}
                showProductType={parentStepNumber === null}
            />

            {sortedBranches.length > 0 && (
                <div className='border-border-subtle ml-3 flex flex-col gap-2 border-l pl-3'>
                    {sortedBranches.map(branch => (
                        <JourneyLogTreeNode
                            key={branch.page.page_id}
                            slot={branch}
                            historyByPageId={historyByPageId}
                            productInfoByPageId={productInfoByPageId}
                            currency={currency}
                            positionByPageId={positionByPageId}
                            relationshipByPageId={relationshipByPageId}
                            parentStepNumber={stepNumber}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

interface JourneyLogRowProps {
    entry: LogEntry;
    info: FunnelPageProductInfo | undefined;
    currency: string;
    relationship: FunnelPageRelationship | undefined;
    parentStepNumber: number | null;
    showProductType: boolean;
}

const JourneyLogRow = ({
    entry,
    info,
    currency,
    relationship,
    parentStepNumber,
    showProductType,
}: JourneyLogRowProps) => {
    const title = info?.title || entry.page.title || 'Untitled';
    const isUnseen = entry.kind === 'unseen';
    const isPurchase = entry.kind === 'visited' && entry.action === 'buy';
    const branchTag =
        relationship?.kind === 'upsell' || relationship?.kind === 'downsell'
            ? getFunnelPageBranchTag(relationship)
            : null;
    const price =
        typeof info?.price === 'number' && info.price > 0
            ? formatCurrency(info.price, info.currency ?? currency)
            : null;
    const typeLabel = info?.type ? (PRODUCT_TYPE_LABELS[info.type as keyof typeof PRODUCT_TYPE_LABELS] ?? null) : null;
    const typeIcon = info?.type
        ? createIconElement(getFunnelPageIcon(info.type), { className: 'size-3 shrink-0' })
        : null;
    const badgeLabel = isUnseen ? 'Not seen' : entry.action === 'buy' ? 'Purchased' : 'Skipped';
    const badgeClass = isUnseen
        ? 'bg-neutral-subtle text-gray'
        : entry.action === 'buy'
          ? 'bg-success-soft text-success'
          : 'bg-neutral-soft text-dark';

    return (
        <div
            className={cn(
                'group border-neutral-subtle flex items-center gap-3 pt-2 transition-[padding] not-first:border-t hover:px-1',
                isUnseen && 'opacity-75'
            )}
        >
            <span className='text-caption-2-bold text-gray group-hover:text-dark w-4 shrink-0 text-center tabular-nums transition-colors'>
                {isUnseen ? '–' : entry.historyIndex + 1}
            </span>
            <div className='border-neutral-subtle bg-neutral-subtle flex size-10 shrink-0 items-center justify-center overflow-hidden rounded-md border'>
                {info?.image ? (
                    <img src={info.image} alt='' className='size-full object-cover' />
                ) : (
                    <IcImage className='text-light size-4' />
                )}
            </div>
            <div className='flex min-w-0 flex-1 flex-col gap-0.5'>
                {branchTag && (
                    <span className='text-caption-2-bold flex min-w-0 items-center gap-1 truncate uppercase'>
                        <span className={cn('font-extrabold', branchTag.colorClass)}>{branchTag.label}</span>
                        {parentStepNumber !== null && <span className='text-gray'>from Page {parentStepNumber}</span>}
                    </span>
                )}
                <span className={cn('!text-caption-1-bold truncate', isUnseen ? 'text-gray' : 'text-dark')}>
                    {title}
                </span>
                {showProductType && typeLabel && (
                    <span className='text-caption-2-regular text-gray flex items-center gap-1'>
                        {typeIcon}
                        {typeLabel}
                    </span>
                )}
            </div>
            <div className='flex shrink-0 flex-col items-end gap-1'>
                <span className={cn('rounded px-1.5 py-0.5 text-[10px] leading-none font-bold uppercase', badgeClass)}>
                    {badgeLabel}
                </span>
                {price && (
                    <span
                        className={cn(
                            '!text-caption-2-bold -translate-x-1',
                            isPurchase ? 'text-dark' : 'text-gray line-through'
                        )}
                    >
                        {price}
                    </span>
                )}
            </div>
        </div>
    );
};
